import {
  Accordion,
  Box,
  Button,
  IconBadgeStatus,
  Stack,
  Typography,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import React from 'react';
import { injectIntl } from 'react-intl';
import { toastr } from 'react-redux-toastr';
import { post } from '../../utils/api';
import useHasPermission from '../../hooks/useHasPermission';
import FinalDecisionForm from './FinalDecisionForm';
import messages from './messages';
import SubmitScreeningModal from './SubmitScreeningModal';
import RunCriminalScreeningModal from './RunCriminalScreeningModal';
import DeclineWithoutCriminalScreeningModal from './DeclineWithoutCriminalScreeningModal';
import SubmitScreeningMenu from './SubmitScreeningMenu';
import {
  isEventFromOutsideCurrentTarget,
  isEventFromSubmitScreeningMenu,
} from './utils';
import DecisionNeededCaption from './DecisionNeededCaption';
import ScreeningProviderFlow from './ScreeningProviderFlow';
import ScreeningErrors from './ScreeningErrors';
import type { Props } from './types';
import {
  useScreeningAccordion,
  useHouseholdScreening,
  useScreeningProviders,
  useActiveProviderPropertyProfiles,
  useValidateApplicants,
  useScreeningStatus,
  useApplicationError,
  useFinalDecision,
  useSubmissionData,
  useDidCompleteAllScreeningMethods,
  useTooltipText,
  useRequestCriminalReport,
  useDeclineWithoutCriminalScreening,
} from './hooks';
import { SCREENING_STATUS_TO_DISABLE_BUTTON } from './constants';
import { getScreeningMenuState } from './screeningMenu';

function CreditCriminalStatusScreening({
  intl,
  householdId,
  applicationId,
  isApplicationCanceledOrDenied,
  providerPropertyProfiles,
  adverseActionDeadlineDays,
  promptToaster,
  screeningLetterTypes,
  organizationId,
  propertyId,
  applicationStatus,
  isResident,
}: Props) {
  const activeProviderPropertyProfiles = useActiveProviderPropertyProfiles(
    providerPropertyProfiles,
  );
  const hasActiveProviderPropertyProfiles =
    activeProviderPropertyProfiles.length > 0;

  const accordion = useScreeningAccordion();

  const [isSubmitScreeningModalOpen, setIsSubmitScreeningModalOpen] =
    React.useState(false);

  const [isRunCriminalScreeningModalOpen, setIsRunCriminalScreeningModalOpen] =
    React.useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = React.useState(false);

  const { householdScreening, refetchHouseholdScreening } =
    useHouseholdScreening({
      householdId,
      isResident,
      enabled: hasActiveProviderPropertyProfiles,
    });

  const { applicantsValidationResult } = useValidateApplicants({
    applicationId,
    enabled: hasActiveProviderPropertyProfiles && !householdScreening,
    isResident,
  });

  const screeningProviders = useScreeningProviders({
    householdScreening,
    activeProviderPropertyProfiles,
    applicantsValidationResult,
    intl,
    applicationStatus,
    isResident,
  });

  const finalDecision = useFinalDecision({
    householdScreening,
  });

  const screeningStatus = useScreeningStatus({
    screeningProviders,
    applicantsValidationResult,
    finalDecision,
    householdScreening,
    isResident,
  });

  const applicationErrors = useApplicationError({
    intl,
    householdScreening,
    screeningProviders,
    applicantsValidationResult,
  });

  const submissionData = useSubmissionData({
    householdScreening,
    screeningStatus,
  });

  const didCompleteAllScreeningMethods = useDidCompleteAllScreeningMethods({
    householdScreening,
  });

  const isSubmitButtonDisabled =
    isApplicationCanceledOrDenied ||
    SCREENING_STATUS_TO_DISABLE_BUTTON.includes(screeningStatus);

  const tooltipText = useTooltipText({
    screeningStatus,
    isApplicationCanceledOrDenied,
  });

  const closeRunCriminalScreeningModal = React.useCallback(
    () => setIsRunCriminalScreeningModalOpen(false),
    [],
  );
  const closeDeclineModal = React.useCallback(
    () => setIsDeclineModalOpen(false),
    [],
  );
  const { isSubmitting: isRequestingCriminalReport, requestCriminalReport } =
    useRequestCriminalReport({
      intl,
      organizationId,
      propertyId,
      applicationId,
      screeningId: householdScreening?.id,
      refetchHouseholdScreening,
      onDone: closeRunCriminalScreeningModal,
    });
  const { isSubmitting: isDeclining, declineWithoutCriminalScreening } =
    useDeclineWithoutCriminalScreening({
      intl,
      organizationId,
      propertyId,
      applicationId,
      screeningId: householdScreening?.id,
      refetchHouseholdScreening,
      onDone: closeDeclineModal,
    });
  const hasCriminalReportPermission = useHasPermission(
    'screening-application-request-criminal-report',
  );
  const menuState = getScreeningMenuState({
    householdScreening,
    activeProviderPropertyProfiles,
    hasCriminalReportPermission,
    isApplicationCanceledOrDenied,
    isSubmitButtonDisabled,
  });
  const showSubmitMenu = menuState.isMenu && !isResident;

  if (!hasActiveProviderPropertyProfiles) {
    return null;
  }

  return (
    <Accordion
      summary={
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          width="100%"
          paddingRight={2}
        >
          <IconBadgeStatus variant={screeningStatus} />
          <Typography variant="bodyLarge">
            {intl.formatMessage(messages.title)}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />
          {showSubmitMenu ? (
            <SubmitScreeningMenu
              intl={intl}
              isDisabled={menuState.isDisabled}
              isDecisionNeeded={menuState.isDecisionNeeded}
              options={menuState.options}
              submitTooltipText={tooltipText}
              onResubmit={() => setIsSubmitScreeningModalOpen(true)}
              onContinue={() => setIsRunCriminalScreeningModalOpen(true)}
              onStop={() => setIsDeclineModalOpen(true)}
            />
          ) : null}
          {!isResident && !showSubmitMenu ? (
            <Tooltip
              placement="top"
              variant="light"
              arrow={false}
              title={tooltipText}
            >
              <Button
                variant="primarySubtle"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsSubmitScreeningModalOpen(true);
                }}
                disabled={isSubmitButtonDisabled}
              >
                {intl.formatMessage(messages.submitScreening)}
              </Button>
            </Tooltip>
          ) : null}
        </Stack>
      }
      expanded={accordion.expanded}
      onChange={(event) => {
        // A click on the Submit Screening menu button, or on its portal, must
        // not toggle the panel. Portal clicks bubble here through the React
        // tree from DOM nodes outside the summary. The guard applies only
        // where the menu shows, so other properties keep their behavior.
        if (
          showSubmitMenu &&
          (isEventFromOutsideCurrentTarget(event) ||
            isEventFromSubmitScreeningMenu(event))
        ) {
          return;
        }
        accordion.toggle();
      }}
      sx={{
        borderBottom: 'none',
      }}
    >
      <Stack spacing={2}>
        {applicationErrors && (
          <ScreeningErrors intl={intl} {...applicationErrors} />
        )}
        <Stack>
          <ScreeningProviderFlow
            intl={intl}
            providers={screeningProviders}
            screeningStatus={screeningStatus}
            phasedCriminalNotice={
              menuState.isDecisionNeeded ? (
                <DecisionNeededCaption intl={intl} />
              ) : null
            }
          />
          {submissionData && (
            <Typography
              variant="bodySmall"
              color="text.secondary"
              alignSelf="flex-end"
              marginTop={0.5}
            >
              {intl.formatMessage(messages.screeningSubmittedOn)}{' '}
              {submissionData.submittedOn} {intl.formatMessage(messages.by)}{' '}
              {submissionData.submittedBy}
            </Typography>
          )}
        </Stack>
        <FinalDecisionForm
          intl={intl}
          screeningId={householdScreening?.id}
          finalScreeningStatus={finalDecision?.finalStatus}
          reasonForDecision={finalDecision?.reason}
          didCompleteAllScreeningMethods={didCompleteAllScreeningMethods}
          refetchHouseholdScreening={refetchHouseholdScreening}
          decisionDate={finalDecision?.decisionDate}
          decisionUserFullName={finalDecision?.decisionUserFullName}
          isApplicationCanceledOrDenied={isApplicationCanceledOrDenied}
          promptToaster={promptToaster}
          adverseActionDeadlineDays={adverseActionDeadlineDays}
          applicationId={applicationId}
          screeningLetterTypes={screeningLetterTypes}
          organizationId={organizationId}
          propertyId={propertyId}
          isResident={isResident}
          screeningData={{
            screeningMethods: Array.from(
              new Map(
                screeningProviders
                  .flatMap((provider) =>
                    provider.methods.map((method) => ({
                      name: method.name,
                      status: method.status,
                    })),
                  )
                  .map((m) => [m.name.toLowerCase(), m]),
              ).values(),
            ),
            screeningStatus,
          }}
        />
      </Stack>
      {menuState.isMenu && (
        <>
          <RunCriminalScreeningModal
            intl={intl}
            open={isRunCriminalScreeningModalOpen}
            isSubmitting={isRequestingCriminalReport}
            onClose={closeRunCriminalScreeningModal}
            onConfirm={requestCriminalReport}
          />
          <DeclineWithoutCriminalScreeningModal
            intl={intl}
            open={isDeclineModalOpen}
            isSubmitting={isDeclining}
            onClose={closeDeclineModal}
            onConfirm={declineWithoutCriminalScreening}
          />
        </>
      )}
      {!isResident && (
        <SubmitScreeningModal
          intl={intl}
          open={isSubmitScreeningModalOpen}
          onClose={() => setIsSubmitScreeningModalOpen(false)}
          onSubmit={(amount) => {
            post(
              `/${organizationId}/${propertyId}/screening-service/submit`,
              JSON.stringify({
                applicationId,
                monthlyRentAmount: amount,
              }),
            )
              .then((response) => {
                toastr.success(
                  intl.formatMessage(messages.success),
                  intl.formatMessage(messages.screeningSubmitted),
                );
                refetchHouseholdScreening();
              })
              .catch(() => {
                toastr.error(
                  intl.formatMessage(messages.error),
                  intl.formatMessage(messages.failedToSubmitScreening),
                );
              });
          }}
        />
      )}
    </Accordion>
  );
}

export default injectIntl(CreditCriminalStatusScreening);
